import { useEffect, useMemo, useState } from 'react'
import { Emoji } from './Emoji.jsx'

// Category icons (emoji used as tab indicators)
const CATEGORY_META = {
  people:    { icon: '😀', label: 'Smileys & People' },
  nature:    { icon: '🐶', label: 'Nature' },
  foods:     { icon: '🍔', label: 'Food & Drink' },
  activity:  { icon: '⚽', label: 'Activities' },
  places:    { icon: '🚗', label: 'Travel & Places' },
  objects:   { icon: '💡', label: 'Objects' },
  symbols:   { icon: '🔣', label: 'Symbols' },
  flags:     { icon: '🏁', label: 'Flags' },
}

export function EmojiPicker({ value, onChange, searchPlaceholder = 'Search…' }) {
  const [data, setData]     = useState(null)
  const [activeCat, setCat] = useState('people')
  const [query, setQuery]   = useState('')

  // Lazy-load the full emoji dataset on mount
  useEffect(() => {
    let alive = true
    import('@emoji-mart/data').then(mod => {
      if (alive) setData(mod.default ?? mod)
    })
    return () => { alive = false }
  }, [])

  // Build the visible list of emoji strings
  const items = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    if (q) {
      // Search: scan all emojis matching name or keyword
      const out = []
      for (const id in data.emojis) {
        const em = data.emojis[id]
        const matches =
          em.name.toLowerCase().includes(q) ||
          em.keywords?.some(k => k.includes(q)) ||
          em.id.includes(q)
        if (matches) {
          const native = em.skins?.[0]?.native
          if (native) out.push(native)
        }
        if (out.length >= 200) break
      }
      return out
    }
    // Filter by active category
    const cat = data.categories.find(c => c.id === activeCat)
    if (!cat) return []
    return cat.emojis
      .map(id => data.emojis[id]?.skins?.[0]?.native)
      .filter(Boolean)
  }, [data, activeCat, query])

  const categories = data?.categories?.filter(c => CATEGORY_META[c.id]) ?? []

  return (
    <div className="emoji-picker">
      {data && (
        <>
          <input
            type="search"
            className="emoji-search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />

          {!query && (
            <div className="emoji-tabs">
              {categories.map(c => (
                <button
                  key={c.id}
                  type="button"
                  className={`emoji-tab${activeCat === c.id ? ' active' : ''}`}
                  onClick={() => setCat(c.id)}
                  aria-label={CATEGORY_META[c.id].label}
                  title={CATEGORY_META[c.id].label}
                >
                  <Emoji symbol={CATEGORY_META[c.id].icon} size="1.2rem" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className="emoji-grid">
        {!data && <span className="emoji-loading">Loading…</span>}
        {data && items.length === 0 && (
          <span className="emoji-loading">No matches</span>
        )}
        {items.map(em => (
          <button
            key={em}
            type="button"
            className={`emoji-cell${value === em ? ' selected' : ''}`}
            onClick={() => onChange(em)}
            aria-label={em}
          >
            <Emoji symbol={em} size="1.4rem" />
          </button>
        ))}
      </div>
    </div>
  )
}
